import { useCallback, useEffect, useState } from "react";
import { Shell } from "../../common/shell/shell.page";
import { PagenedResult } from "../../common/pagened-result";
import { Product } from "../product.entity";
import { productRepository } from "../product.repository";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import "./products.page.scss";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { SwalReact } from "../../common/swal-react";
import { ProductCategory } from "../product-category.entity";
import { ProductSupplier } from "../product-supplier.entity";
import { productCategoriesRepository } from "../product-categories.repository";
import { productSupplierRepository } from "../product-supplier.repository";
import application from "../../core/application";
import { Permission } from "../../users/user.entity";
import { PageHeader } from "../../common/components/page-header";
import { LoadingOverlay } from "../../common/components/loading-overlay";
import { useModuleAccess } from "../../settings/module-access";
import { ModuleLockedView } from "../../settings/components/module-locked-view";

const PRODUCTS_PAGENED_LIMIT = 15;

const formatBRL = (n: number) =>
  n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const calcMarginPct = (salePrice: number, purchasePrice: number): number | null => {
  if (purchasePrice <= 0 || salePrice <= 0) return null;
  return ((salePrice - purchasePrice) / purchasePrice) * 100;
};


export const ProductsPage = () => {
  const theme = useTheme();
  const [productsPagened, setProductsPagened] = useState<PagenedResult<Product>>();
  const [hasNext, setHasNext] = useState<boolean>(true);
  const [hasPrevius, setHasPrevius] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [suppliers, setSuppliers] = useState<ProductSupplier[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [supplierFilter, setSupplierFilter] = useState<string>("all");

  const [user] = application.user.useState();
  const [totalProducts, setTotalProducts] = useState(0);
  const moduleAccess = useModuleAccess();

  application.useHasPermission([Permission.SELL, Permission.WRITE_PRODUCTS]);

  useEffect(() => {
    (async () => {
      const [paged, all, cats, sups] = await Promise.all([
        productRepository.getAllPagened(PRODUCTS_PAGENED_LIMIT, "name"),
        productRepository.getAll("name"),
        productCategoriesRepository.getAll(),
        productSupplierRepository.getAll(),
      ]);
      setProductsPagened(paged);
      setTotalProducts(all.length);
      setAllProducts(all);
      setCategories(cats);
      setSuppliers(sups);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!productsPagened) return;
      setHasNext(await productsPagened.hasNext());
      setHasPrevius(await productsPagened.hasPrevius());
    })();
  }, [productsPagened]);

  const nextPage = useCallback(async () => {
    if (!productsPagened) return;
    setProductsPagened(await productsPagened.next());
  }, [productsPagened]);

  const previusPage = useCallback(async () => {
    if (!productsPagened) return;
    setProductsPagened(await productsPagened.previus());
  }, [productsPagened]);

  const removeProduct = useCallback(
    async (product: Product) => {
      if (!productsPagened) return;
      const result = await Swal.fire({
        title: `Excluir "${product.getName()}"?`,
        text: "Esta ação não pode ser desfeita.",
        showCancelButton: true,
        confirmButtonText: "Sim, excluir",
        cancelButtonText: "Cancelar",
        icon: "warning",
      });
      if (!result.isConfirmed) return;
      await productRepository.remove(product);
      setProductsPagened(await productsPagened.refreshItens());
    },
    [productsPagened]
  );

  const openSearchByCode = () => {
    SwalReact.fire({
      title: "Abrir produto pelo código",
      input: "text",
      inputPlaceholder: "Código do produto",
      showCancelButton: true,
      confirmButtonText: "Abrir",
      cancelButtonText: "Cancelar",
      preConfirm: async (value) => {
        if (!value) { SwalReact.showValidationMessage("Informe o código."); return; }
        const found = await productRepository.findByCodeOrLegacyDocumentId(value.trim());
        if (!found) { SwalReact.showValidationMessage("Produto não encontrado."); return; }
        navigate(`/produtos/editar/${found.id}`);
      },
    });
  };

  if (loading || moduleAccess.loading) {
    return (
      <Shell title="Produtos">
        <LoadingOverlay message="Carregando produtos…" subtle />
      </Shell>
    );
  }

  const canWrite = user?.hasPermission([Permission.WRITE_PRODUCTS]);
  const productsEnabled =
    moduleAccess.isEnabled("products_sales_basic") ||
    moduleAccess.isEnabled("products_sales_advanced") ||
    moduleAccess.isEnabled("products_sales_unlimited");
  const productsLimit = moduleAccess.limits.productsLimit;
  const limitReached = Number.isFinite(productsLimit) && totalProducts >= productsLimit;

  if (!productsEnabled) {
    return (
      <Shell title="Produtos">
        <PageHeader title="Produtos" description="Módulo de produtos e vendas." />
        <ModuleLockedView
          moduleName="Cadastro de produtos e vendas"
          groupId="products_sales"
          details="Ative uma variação (Básico, Avançado ou Ilimitado) para usar catálogo de produtos e vendas."
          features={[
            "Catálogo de produtos personalizado",
            "Registro de vendas ilimitadas",
            "Controle de preços e variações",
          ]}
        />
      </Shell>
    );
  }

  const searchActive = searchTerm.trim().length > 0;
  const filterActive = categoryFilter !== "all" || supplierFilter !== "all";
  const baseList = (searchActive || filterActive) ? allProducts : (productsPagened?.itens ?? []);
  const products = baseList
    .filter(p =>
      !searchActive ||
      p.getName().toLowerCase().includes(searchTerm.trim().toLowerCase()) ||
      (p.getCode() ?? "").toLowerCase().includes(searchTerm.trim().toLowerCase())
    )
    .filter(p => categoryFilter === "all" || p.getCategory()?.id === categoryFilter)
    .filter(p =>
      supplierFilter === "all" ||
      p.getSupplierCosts().some(c => c.supplier.id === supplierFilter)
    );

  return (
    <Shell title="Produtos">
      <PageHeader
        title="Produtos"
        description="Consulte, filtre e edite o catálogo de produtos."
        action={
          canWrite ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/produtos/novo")}
              disabled={limitReached}
            >
              Novo produto
            </Button>
          ) : undefined
        }
      />

      {/* Hero */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          border: 1,
          borderColor: "divider",
          borderRadius: 3,
          overflow: "hidden",
          position: "relative",
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.dark, 0.02)} 100%)`,
        }}
      >
        <Box sx={{ position: "absolute", top: -20, right: -20, opacity: 0.04, pointerEvents: "none" }}>
          <Inventory2OutlinedIcon sx={{ fontSize: 180 }} />
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: "white",
              flexShrink: 0,
            }}
          >
            <Inventory2OutlinedIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box flex={1}>
            <Typography variant="h5" fontWeight={800} letterSpacing="-0.02em">
              Catálogo de produtos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gerencie preços, custos, estoque e fornecedores de cada item.
            </Typography>
          </Box>
          <Chip
            icon={<Inventory2OutlinedIcon />}
            label={
              Number.isFinite(productsLimit)
                ? `${totalProducts} de ${productsLimit} produtos`
                : `${totalProducts} produto${totalProducts !== 1 ? "s" : ""}`
            }
            color={limitReached ? "warning" : "default"}
            variant={limitReached ? "filled" : "outlined"}
          />
        </Stack>
      </Paper>

      {limitReached && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Limite do plano atingido. Faça upgrade do módulo de produtos para continuar cadastrando.
        </Alert>
      )}

      {/* Toolbar */}
      <Paper
        elevation={0}
        sx={{ p: 2, mb: 2, border: 1, borderColor: "divider", borderRadius: 2 }}
      >
        <Stack spacing={2}>
          <TextField
            size="small"
            fullWidth
            placeholder="Buscar produto por nome ou código…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("")} edge="end">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ sm: "center" }}
            flexWrap="wrap"
            useFlexGap
          >
            <FormControl size="small" sx={{ minWidth: 160 }} disabled={categories.length === 0}>
              <InputLabel>Categoria</InputLabel>
              <Select
                value={categoryFilter}
                label="Categoria"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="all">Todas as categorias</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }} disabled={suppliers.length === 0}>
              <InputLabel>Fornecedor</InputLabel>
              <Select
                value={supplierFilter}
                label="Fornecedor"
                onChange={(e) => setSupplierFilter(e.target.value)}
              >
                <MenuItem value="all">Todos os fornecedores</MenuItem>
                {suppliers.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.title}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {filterActive && (
              <Button
                size="small"
                variant="text"
                startIcon={<CloseIcon fontSize="small" />}
                onClick={() => { setCategoryFilter("all"); setSupplierFilter("all"); }}
                color="inherit"
                sx={{ color: "text.secondary" }}
              >
                Limpar filtros
              </Button>
            )}

            <Box flex={1} />

            {canWrite && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<QrCode2Icon />}
                onClick={openSearchByCode}
              >
                Abrir por código
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Estado vazio */}
      {products.length === 0 && (
        <Paper elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
          <Box sx={{ py: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <Inventory2OutlinedIcon sx={{ fontSize: 48, color: "text.disabled" }} />
            <Typography color="text.secondary">
              {filterActive || searchActive
                ? "Nenhum produto encontrado para os filtros selecionados."
                : "Nenhum produto cadastrado."}
            </Typography>
            {canWrite && !filterActive && !searchActive && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => navigate("/produtos/novo")}
                disabled={limitReached}
              >
                Cadastrar primeiro produto
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {/* Mobile: cards (xs, sm) */}
      {products.length > 0 && (
        <Stack spacing={1.5} sx={{ display: { xs: "flex", md: "none" } }}>
          {products.map((product) => {
            const costs = product.getSupplierCosts();
            const stock = product.getTrackStock() ? product.getStock() : null;
            const margins = costs
              .map((c) => calcMarginPct(product.getPrice(), c.purchasePrice))
              .filter((m): m is number => m !== null);
            const bestMargin = margins.length > 0 ? Math.max(...margins) : null;
            return (
              <Paper
                key={product.id}
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 2,
                  cursor: canWrite ? "pointer" : "default",
                  "&:hover": canWrite ? { bgcolor: "action.hover" } : undefined,
                }}
                onClick={canWrite ? () => navigate(`/produtos/editar/${product.id}`) : undefined}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Avatar
                    variant="rounded"
                    src={product.getImage()}
                    alt=""
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: "primary.main",
                      flexShrink: 0,
                      fontSize: 20,
                      fontWeight: 700,
                    }}
                  >
                    {(product.getName() || "?")[0].toUpperCase()}
                  </Avatar>

                  <Box flex={1} minWidth={0}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                      <Box minWidth={0}>
                        <Typography fontWeight={600}>
                          {product.getName() || "Sem nome"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {product.getCodeLabelForUi()}
                        </Typography>
                      </Box>
                      {canWrite && (
                        <Stack direction="row" spacing={0.25} flexShrink={0} onClick={(e) => e.stopPropagation()}>
                          <IconButton size="small" color="primary" onClick={() => navigate(`/produtos/editar/${product.id}`)}>
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => removeProduct(product)}>
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Stack>
                      )}
                    </Stack>

                    {/* Preço de venda + margem */}
                    <Box sx={{ mt: 1, mb: costs.length > 0 ? 1 : 0 }}>
                      <Typography variant="caption" color="text.secondary">Preço de venda</Typography>
                      <Typography variant="body1" fontWeight={800} color="primary.main">
                        {formatBRL(product.getPrice())}
                      </Typography>
                      {bestMargin !== null && (
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                          <TrendingUpIcon sx={{ fontSize: 13, color: bestMargin < 30 ? "warning.main" : "success.main" }} />
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            color={bestMargin < 30 ? "warning.main" : "success.main"}
                          >
                            {bestMargin.toFixed(0)}% margem
                          </Typography>
                        </Stack>
                      )}
                    </Box>

                    {/* Fornecedores e custos */}
                    {costs.length > 0 && (
                      <Box
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          border: 1,
                          borderColor: alpha(theme.palette.primary.main, 0.15),
                          borderRadius: 1.5,
                          p: 1.25,
                          mb: 1,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.75, display: "block", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                          Custo por fornecedor
                        </Typography>
                        <Stack spacing={0.75}>
                          {costs.map((cost, i) => (
                            <Stack key={i} direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: "55%" }}>
                                {cost.supplier.title}
                              </Typography>
                              <Typography variant="body2" fontWeight={800} color="text.primary">
                                {formatBRL(cost.purchasePrice)}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {/* Categoria + Estoque */}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {product.getCategory() && (
                        <Chip
                          label={product.getCategory()!.title}
                          size="small"
                          variant="outlined"
                          sx={{ height: 22, fontSize: "0.7rem" }}
                        />
                      )}
                      {stock !== null && (
                        <Chip
                          size="small"
                          label={
                            stock <= 0
                              ? "Sem estoque"
                              : stock <= 3
                              ? `Estoque baixo: ${stock}`
                              : `Estoque: ${stock}`
                          }
                          color={stock <= 0 ? "error" : stock <= 3 ? "warning" : "default"}
                          variant={stock <= 3 ? "filled" : "outlined"}
                          sx={{ height: 22, fontSize: "0.7rem" }}
                        />
                      )}
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Desktop: tabela (md+) */}
      {products.length > 0 && (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: 1, borderColor: "divider", borderRadius: 2, display: { xs: "none", md: "block" } }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "action.hover" }}>
                <TableCell sx={{ fontWeight: 700 }}>Produto</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Preço de venda</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Fornecedores / Custo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Categoria</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Estoque</TableCell>
                {canWrite && <TableCell align="right" sx={{ width: 80 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((product) => {
                const costs = product.getSupplierCosts();
                const margins = costs
                  .map((c) => calcMarginPct(product.getPrice(), c.purchasePrice))
                  .filter((m): m is number => m !== null);
                const bestMargin = margins.length > 0 ? Math.max(...margins) : null;
                return (
                  <TableRow
                    key={product.id}
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                      "&:hover": { bgcolor: "action.hover" },
                      cursor: canWrite ? "pointer" : "default",
                    }}
                    onClick={canWrite ? () => navigate(`/produtos/editar/${product.id}`) : undefined}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          variant="rounded"
                          src={product.getImage()}
                          alt=""
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: "primary.main",
                            flexShrink: 0,
                            fontSize: 18,
                            fontWeight: 700,
                          }}
                        >
                          {(product.getName() || "?")[0].toUpperCase()}
                        </Avatar>
                        <Box minWidth={0}>
                          <Typography fontWeight={600} noWrap>
                            {product.getName() || "Sem nome"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {product.getCodeLabelForUi()}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Typography fontWeight={700} color="primary.main" noWrap>
                        {formatBRL(product.getPrice())}
                      </Typography>
                      {bestMargin !== null && (
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                          <TrendingUpIcon sx={{ fontSize: 12, color: bestMargin < 30 ? "warning.main" : "success.main" }} />
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            color={bestMargin < 30 ? "warning.main" : "success.main"}
                          >
                            {bestMargin.toFixed(0)}% margem
                          </Typography>
                        </Stack>
                      )}
                    </TableCell>

                    <TableCell>
                      {costs.length === 0 ? (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      ) : (
                        <Stack spacing={0.75}>
                          {costs.map((cost, i) => (
                            <Box key={i}>
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 180, display: "block" }}>
                                {cost.supplier.title}
                              </Typography>
                              <Typography variant="body2" fontWeight={800} color="text.primary" noWrap>
                                {formatBRL(cost.purchasePrice)}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </TableCell>

                    <TableCell>
                      {product.getCategory() ? (
                        <Chip
                          label={product.getCategory()!.title}
                          size="small"
                          variant="outlined"
                          sx={{ height: 22, fontSize: "0.7rem" }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {product.getTrackStock() ? (
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color={
                              product.getStock() <= 0
                                ? "error.main"
                                : product.getStock() <= 3
                                ? "warning.main"
                                : "text.primary"
                            }
                          >
                            {product.getStock()}
                          </Typography>
                          {product.getStock() <= 0 && (
                            <Typography variant="caption" color="error.main">Sem estoque</Typography>
                          )}
                          {product.getStock() > 0 && product.getStock() <= 3 && (
                            <Typography variant="caption" color="warning.main">Baixo</Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>

                    {canWrite && (
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Editar">
                            <IconButton size="small" color="primary" onClick={() => navigate(`/produtos/editar/${product.id}`)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton size="small" color="error" onClick={() => removeProduct(product)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Paginação */}
      {!searchActive && !filterActive && (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
          <Button variant="outlined" disabled={hasPrevius} onClick={previusPage} fullWidth>
            Anterior
          </Button>
          <Button variant="outlined" disabled={hasNext} onClick={nextPage} fullWidth>
            Próxima
          </Button>
        </Stack>
      )}

      {(searchActive || filterActive) && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          {products.length} resultado{products.length !== 1 ? "s" : ""}
          {searchActive ? ` para "${searchTerm}"` : ""}
        </Typography>
      )}
    </Shell>
  );
};
